import React, { useEffect } from 'react';
import { Autocomplete, TextField, InputAdornment } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { textFieldStyles } from "../styles/textFieldStyles";
import { CustomPaper } from '../styles/customPaper';
import { Avatar, Box, Typography, Stack } from "@mui/material";
import '../styles/customScrollbar.css'; // Import the custom scrollbar styles

const CustomAutocomplete = ({ required, disabled, options, value, onChange, label, filterType, addNewLabel, addNewPath, index, showIcon, showAddNewOption, onAddNew, icon: Icon }) => {
  const { t } = useTranslation(['common', 'buttons']);
  const navigate = useNavigate();

  // Enhanced debug logging to track relationship refresh issues
  useEffect(() => {
    if (filterType === 'Party') {
      console.log(`🔍 CustomAutocomplete Debug - ${label}:`, {
        showAddNewOption,
        filterType,
        optionsLength: Array.isArray(options) ? options.length : 0,
        addNewLabel,
        hasAddNewInOptions: Array.isArray(options) ? options.some(opt => opt.contractType === "AddNewIdentity") : false,
        partyOptions: Array.isArray(options) ? options.filter(opt => opt.type !== 'Lawyer').length : 0,
        firstFewPartyIds: Array.isArray(options) ?
          options.filter(opt => opt.type !== 'Lawyer').slice(0, 3).map(opt => ({ id: opt._id, name: opt.name || opt.businessName })) : [],
        currentValue: value ? { id: value._id, name: value.name || value.businessName } : null
      });
    }
  }, [options, showAddNewOption, filterType, label, addNewLabel, value]);

  const handleAddNew = () => {
    console.log('🚀 CustomAutocomplete handleAddNew called');
    if (onAddNew) {
      console.log('📞 Calling custom onAddNew handler');
      onAddNew();
    } else {
      console.log('🧭 Using default navigation to:', addNewPath);
      navigate(addNewPath);
    }
  };

  // Filter options based on type without artificial limits
  const typeFilteredOptions = Array.isArray(options) ? options.filter((option) => {
    if (filterType === 'Member') {
      return option.type === 'Lawyer';
    } else if (filterType === 'Party') {
      return option.type !== 'Lawyer';
    }
    return true;
  }) : [];

  // Helper function to create a unique key for a relationship
  // This helps identify duplicates even when they have different _id's
  const getRelationshipKey = (option) => {
    if (!option) return '';
    
    // Use uniqueNumber if available (most reliable identifier)
    if (option.uniqueNumber) {
      return `uniqueNumber_${option.uniqueNumber}`;
    }
    
    // Fallback to name + email combination for deduplication
    // For individuals: use firstName + middleName + lastName
    // For companies: use businessName
    const name = option.contractType === "Individual"
      ? [option.firstName, option.middleName, option.lastName].filter(Boolean).join(" ").toLowerCase().trim()
      : (option.businessName || '').toLowerCase().trim();
    
    const email = (option.email || '').toLowerCase().trim();
    
    // If we have both name and email, use both for more accurate matching
    // If only name exists, use just name (but this is less reliable)
    if (name && email) {
      return `name_${name}_email_${email}`;
    } else if (name) {
      return `name_${name}`;
    }
    
    // If no name or email, fall back to _id (shouldn't happen in normal cases)
    return `id_${option._id}`;
  };

  // Deduplicate options using Map for better performance and reliability
  // This handles cases where the same person might have multiple relationship records
  const deduplicatedOptions = (() => {
    const idMap = new Map(); // Track by _id first
    const keyMap = new Map(); // Then track by semantic key
    
    for (const option of typeFilteredOptions) {
      if (!option || !option._id) continue;
      
      // Skip if we've already seen this exact _id
      if (idMap.has(option._id)) {
        continue;
      }
      
      // Create a semantic key for this option
      const currentKey = getRelationshipKey(option);
      
      // Check if we've seen this semantic key before
      if (keyMap.has(currentKey) && !currentKey.startsWith('id_')) {
        // We have a duplicate by semantic key
        const existing = keyMap.get(currentKey);
        // Prefer the one with uniqueNumber
        if (option.uniqueNumber && !existing.uniqueNumber) {
          // Replace existing with current
          keyMap.set(currentKey, option);
          idMap.set(option._id, option);
          // Remove the old one
          idMap.delete(existing._id);
        }
        // Otherwise keep the existing one
        continue;
      }
      
      // New option - add it
      idMap.set(option._id, option);
      keyMap.set(currentKey, option);
    }
    
    // Return array of unique options
    return Array.from(idMap.values());
  })();

  // Add the "Add New" option if enabled (at the TOP of the list)
  const filteredOptions = [
    ...(showAddNewOption ? [{ _id: `add-${filterType?.toLowerCase()}`, contractType: "AddNewIdentity" }] : []),
    ...deduplicatedOptions,
  ];

  console.log(`📋 ${label} - CustomAutocomplete filteredOptions:`, {
    totalOptionsReceived: Array.isArray(options) ? options.length : 0,
    afterTypeFiltering: typeFilteredOptions.length,
    afterDeduplication: deduplicatedOptions.length,
    finalCount: filteredOptions.length,
    addNewOptionIncluded: filteredOptions.some(opt => opt.contractType === "AddNewIdentity"),
    addNewOptionId: `add-${filterType?.toLowerCase()}`,
    showAddNewOption,
    filterType,
    duplicatesRemoved: typeFilteredOptions.length - deduplicatedOptions.length
  });
  
  // Log details about each option for debugging duplicates
  if (label && (label.includes('Owner') || label.includes('owner'))) {
    console.log(`🔍 ${label} - Detailed options list:`, filteredOptions.map(opt => ({
      _id: opt._id,
      name: opt.contractType === "Individual"
        ? `${opt.firstName || ''} ${opt.middleName || ''} ${opt.lastName || ''}`.trim()
        : opt.businessName,
      uniqueNumber: opt.uniqueNumber,
      email: opt.email,
      type: opt.type,
      key: opt._id === `add-${filterType?.toLowerCase()}` ? 'ADD_NEW' : (
        opt.uniqueNumber 
          ? `unique_${opt.uniqueNumber}`
          : `name_${(opt.contractType === "Individual"
              ? [opt.firstName, opt.middleName, opt.lastName].filter(Boolean).join(" ").toLowerCase().trim()
              : (opt.businessName || '').toLowerCase().trim())}_email_${(opt.email || '').toLowerCase().trim()}`
      )
    })));
  }

  // Performance monitoring for large datasets
  if (filteredOptions.length > 100) {
    console.log(`⚠️ ${label} has ${filteredOptions.length} options - performance may be impacted. Consider implementing search/filtering.`);
  }

  return (
    <Autocomplete
      options={filteredOptions}
      size="small"
      // Disable default filtering - we handle it ourselves
      filterOptions={(options, state) => {
        if (!state.inputValue) {
          return options;
        }

        const inputLower = state.inputValue.toLowerCase().trim();
        const seenIds = new Set();
        const filtered = [];

        for (const option of options) {
          // Absolutely skip if we've seen this _id
          if (seenIds.has(option._id)) {
            continue;
          }

          const optionLabel = option.contractType === "AddNewIdentity"
            ? (addNewLabel || '')
            : (option.contractType === "Individual"
                ? [option.firstName, option.middleName, option.lastName].filter(Boolean).join(" ")
                : option.businessName || '');
          
          const email = option.email || '';
          const fullLabel = `${optionLabel} ${email}`.toLowerCase();

          if (fullLabel.includes(inputLower) || optionLabel.toLowerCase().includes(inputLower)) {
            seenIds.add(option._id);
            filtered.push(option);
          }
        }

        return filtered;
      }}
      // Enable virtualization for better performance with large datasets
      disableListWrap
      // Set a reasonable max height for the dropdown
      ListboxProps={{
        className: 'MuiAutocomplete-listbox custom-scrollbar',
        style: { maxHeight: '300px' }, // Increased from default to show more options
        onScroll: (event) => {
          const listboxNode = event.currentTarget;
          if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
            // Scroll reached bottom - could implement pagination here if needed
            console.log(`📜 ${label} dropdown scrolled to bottom`);
          }
        }
      }}
      onInputChange={(event, value, reason) => {
        if (label && (label.includes('Owner') || label.includes('owner'))) {
          console.log(`⌨️ ${label} - Input changed:`, {
            value,
            reason,
            filteredOptionsCount: filteredOptions.length
          });
        }
      }}
      onOpen={() => {
        if (label && (label.includes('Owner') || label.includes('owner'))) {
          console.log(`📂 ${label} - Dropdown opened with ${filteredOptions.length} options`);
        }
      }}
      getOptionLabel={(option) => {
        if (option.contractType === "AddNewIdentity") {
          return addNewLabel;
        }

        const name = option.contractType === "Individual"
          ? [option.firstName, option.middleName, option.lastName].filter(Boolean).join(" ")
          : option.businessName || '';

        const email = option.email ? ` - ${option.email}` : '';
        return `${name}${email}`;
      }}
      isOptionEqualToValue={(option, value) => {
        if (!value) return false;
        // Compare by _id to properly identify unique options
        return option._id === value._id;
      }}
      value={value}
      disabled={disabled}
      onChange={(event, newValue) => {
        console.log(`🎯 ${label} onChange triggered:`, {
          newValue: newValue ? {
            id: newValue._id,
            name: newValue.name || newValue.businessName || `${newValue.firstName || ''} ${newValue.lastName || ''}`.trim(),
            type: newValue.type,
            contractType: newValue.contractType
          } : null,
          isAddNewOption: newValue && newValue._id === `add-${filterType?.toLowerCase()}`,
          expectedAddNewId: `add-${filterType?.toLowerCase()}`,
          totalOptionsAvailable: filteredOptions.length
        });

        if (newValue && newValue._id === `add-${filterType?.toLowerCase()}`) {
          console.log('✅ Add new option detected, calling handleAddNew');
          handleAddNew();
        } else {
          console.log('📝 Regular option selected, calling parent onChange');
          onChange(event, newValue);
        }
      }}
      sx={textFieldStyles()}
      PaperComponent={CustomPaper}

      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          required
          InputProps={{
            ...params.InputProps,
            startAdornment: value ? ( // Show avatar only if an option is selected
              <Avatar
                src={value.avatar}
                // sx={{ width: 32, height: 32, marginRight: 1 }}
                sx={{
                  width: "1rem", // Set width to 1rem
                  height: "1rem", // Set height to 1rem
                  fontSize: "7.8px", // Set font size to 8px
                  // marginRight: 1,
                }}
              >
                {/* Show the first letter of first or middle name (fallback to last name if no middle name) */}
                {!value.avatar &&
                  `${value.firstName?.charAt(0) || ""}${value.middleName
                    ? value.middleName.charAt(0)
                    : value.lastName?.charAt(0) || ""
                  }`}
              </Avatar>
            ) : null,
          }}
        />
      )}

      // renderInput={(params) => (
      //   <TextField
      //     {...params}
      //     label={label}
      //     variant="outlined"
      //     required // Set the required prop based on the condition

      //     InputProps={{
      //       ...params.InputProps,
      //       sx: {
      //         // further adjustments to inner padding
      //         padding: "0 20px",
      //         // boxShadow: "none",
      //       },
      //       startAdornment: showIcon && Icon ? (
      //         <InputAdornment position="start">
      //           <Icon />
      //         </InputAdornment>
      //       ) : null,
      //     }}
      //   />
      // )}
      // renderOption={(props, option) => (
      //   <li {...props} >
      //     {option.contractType === "AddNewIdentity" ? (
      //       <span style={{ color: "#2B80B6", display: "flex", alignItems: "center" }}>
      //       <AddIcon sx={{ mr: 1 }} />
      //       {addNewLabel}
      //     </span>
      //     ) : (
      //       `${option.uniqueNumber ? option.uniqueNumber + " - " : ""}${option.firstName || ''} ${option.middleName || ''} ${option.lastName || ''}${option.businessName || ''}`
      //     )}
      //   </li>
      // )}

      renderOption={(props, option) => {
        // Debug log for each rendered option
        if (label && (label.includes('Owner') || label.includes('owner'))) {
          const optionName = option.contractType === "Individual"
            ? `${option.firstName || ''} ${option.middleName || ''} ${option.lastName || ''}`.trim()
            : option.businessName;
          console.log(`🎨 Rendering option:`, {
            _id: option._id,
            name: optionName,
            uniqueNumber: option.uniqueNumber,
            key: props.key
          });
        }
        
        return (
          <li {...props} style={{ fontSize: "10.5px", padding: "0 4px" }} key={option._id}>
            {option.contractType === "AddNewIdentity" ? (
              <span style={{ color: "#2B80B6", display: "flex", alignItems: "center", fontSize: "10.5px" }}>
                <AddIcon sx={{ mr: 1 }} />
                {addNewLabel}
              </span>
            ) : (
              <Box display="flex" alignItems="center" sx={{ pl: 1 }}>
                <Avatar
                  src={option.avatar} // Make sure option.avatar contains the image URL
                  sx={{
                    width: "1rem", // Set width to 1rem
                    height: "1rem", // Set height to 1rem
                    fontSize: "7.8px", // Set font size to 8px
                    marginRight: 1, // Add margin-right of 1
                  }}
                >
                  {/* Show the first letter of first or middle name (fallback to last name if no middle name) */}
                  {!option.avatar &&
                    `${option.firstName?.charAt(0) || ""}${option.middleName
                      ? option.middleName.charAt(0)
                      : option.lastName?.charAt(0) || ""
                    }`}
                </Avatar>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Typography sx={{ fontSize: "10.5px", lineHeight: 1, fontWeight: "bold" }}>
                    {`${option.firstName || ""} ${option.middleName || ""
                      } ${option.lastName || ""}${option.businessName || ""}`}
                  </Typography>
                  {option.email && (
                    <Typography sx={{ fontSize: "9px", lineHeight: 1, color: "textSecondary", mt: 0.2 }}>
                      {option.email}
                    </Typography>
                  )}
                  {/* Debug info - remove in production */}
                  <Typography sx={{ fontSize: "7px", lineHeight: 1, color: "gray", mt: 0.2 }}>
                    ID: {option._id?.substring(0, 8)}... | #{option.uniqueNumber || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            )}
          </li>
        );
      }}
    />
  );
};

export default CustomAutocomplete;